import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { LayerSymbologyType, LineStyle } from 'src/app/shared/map-data-services/styles/style-symbology';
import { GeometryTypes } from '../../utility/geometry-utils';

@Component({
    templateUrl: './gsp-geom-icon.component.html',
    selector: 'geom-icon',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GeometryIconComponent {
    private _geoType: string;
    private _color: string;
    private _outlineColor: string;
    private _path: string;

    @Input()
    public set geoType(type: string) {
        if (type && type === GeometryTypes.AREA) {
            this._geoType = 'poly';
        } else {
            this._geoType = type;
        }
    }

    public get geoType(): string {
        return this._geoType || 'Point';
    }

    @Input()
    public set color(color: string) {
        this._color = color;
    }

    public get color(): string {
        return this._color || '#000000';
    }

    @Input()
    public set outlineColor(color: string) {
        this._outlineColor = color;
    }

    public get outlineColor(): string {
        return this._outlineColor || '#000000';
    }

    @Input()
    public set path(path: string) {
        this._path = path;
    }

    @Input()
    public svgIcon: string = '';

    @Input()
    public symbolType: LayerSymbologyType = LayerSymbologyType.GEOMETRIC;

    @Input()
    public symbolText: string = '';

    @Input()
    public lineStyle: LineStyle = LineStyle.SOLID;

    @Input()
    public polygonOpacity: number = 20;

    @Input()
    public addLayerBasePolygon: boolean = false;

    @Input()
    size: number = 24;

    // Export enum to template
    public LineStyle = LineStyle;

    constructor(private sanitizer: DomSanitizer) {}

    public get path(): string {
        return this._path ? this._path : 'symbol_24-geom-';
    }

    public get svg(): string {
        return this.path + this.geoType.toLowerCase();
    }

    public get svgIconImage(): SafeHtml {
        if (!this.svgIcon || this.geoType !== GeometryTypes.POINT) {
            return '';
        }

        const layerBaseIcon = `
            <polygon opacity="0.1" fill-rule="evenodd" clip-rule="evenodd" fill="#363545" points="6,38 6,31.3 32,19 58,31.3 58,38 32,26 " />
            <polygon opacity="0.3" fill-rule="evenodd" clip-rule="evenodd" fill="#363545" points="6,31 6,37.7 32,50 58,37.7 58,31 32,43 " />
        `;

        const iconContainer = document.createElement('div');
        iconContainer.innerHTML = this.svgIcon;

        const svgElement = iconContainer.querySelector('svg');

        if (svgElement) {
            svgElement.setAttribute('width', this.size.toString());
            svgElement.setAttribute('height', this.size.toString());
            if (this.addLayerBasePolygon) {
                svgElement.innerHTML = layerBaseIcon + svgElement.innerHTML;

                // Move feature symbol on top of the base icon
                const featureSymbolGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
                featureSymbolGroup.setAttribute('transform', 'translate(18, 10) scale(1.2)');

                const children = Array.from(svgElement.children);
                children.forEach(child => {
                    if (child.tagName !== 'polygon') {
                        // Exclude polygons (layerBaseIcon)
                        featureSymbolGroup.appendChild(child);
                    }
                });

                svgElement.appendChild(featureSymbolGroup);

                svgElement.setAttribute('viewBox', '0 0 64 64');
                svgElement.removeAttribute('width');
                svgElement.removeAttribute('height');
            }

            const definitionIcons = svgElement.querySelectorAll('.sd-icon');

            if (this.symbolType !== LayerSymbologyType.CUSTOMIZABLE) {
                definitionIcons.forEach(icon => {
                    if (icon.classList.contains('sd-fill')) {
                        icon.setAttribute('fill', this.color);
                    }
                    if (icon.classList.contains('sd-stroke')) {
                        icon.setAttribute('stroke', this.outlineColor);
                    }
                });
            } else {
                definitionIcons.forEach(icon => {
                    if (icon.classList.contains('sd-fill')) {
                        icon.setAttribute('fill', '#ffffff');
                    }
                    if (icon.classList.contains('sd-stroke')) {
                        icon.setAttribute('stroke', this.outlineColor);
                    }
                });

                const textIcons = svgElement.querySelectorAll('.symbology-customizable-icon-text');
                textIcons.forEach(text => {
                    text.setAttribute('fill', this.outlineColor);
                    text.textContent = this.symbolText;
                });
            }

            return this.sanitizer.bypassSecurityTrustHtml(svgElement.outerHTML);
        } else {
            this.path = 'symbol_24-geom-none';
        }
    }
}
